import {
  Box,
  MenuItem,
  SxProps,
  TextField,
  Badge,
  styled,
} from '@mui/material';
import React, { FC, useState, useMemo } from 'react';
import { OptionType } from '../../utils/type';
import MediumTypography from './MediumTypography';
import { useIntl } from 'react-intl';
import DropdownIconComponent from './DropdownIconComponent';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 10;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      scroll: 'hidden',
      maxWidth: 'min-content',
    },
  },
};

const CustomBadge = styled(Badge)(({ color }: { color?: string }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: color ?? '#37D183',
    height: '8px',
    width: '8px',
    top: 0,
    bottom: 0,
  },
}));

interface DropdownComponentProps {
  optionalBadge?: boolean;
  name?: string;
  id?: string;
  value?: string;
  handleChange?: (value: string) => void;
  names: OptionType[];
  defaultValue?: string;
  disabled?: boolean;
  labelid?: string;
  defaultlabelid?: string;
  sxProps?: SxProps;
  className?: string;
  Required?: boolean;
  onHoverBorderColor?: string;
  onFocusedBorderColor?: string;
  placeholderOnfloatBackgroundColor?: string;
  disableClearable?: boolean;
  disabledColor?: string;
}

const DropdownComponent: FC<DropdownComponentProps> = (props) => {
  const {
    id,
    name,
    value,
    names,
    Required,
    className,
    defaultValue,
    defaultlabelid,
    disableClearable,
    disabled,
    handleChange,
    labelid,
    optionalBadge,
    onHoverBorderColor,
    onFocusedBorderColor,
    placeholderOnfloatBackgroundColor,
    sxProps,
    disabledColor,
  } = props;
  const rctl = useIntl();
  const [open, setOpen] = useState<boolean>(false);

  const IconComponent = useMemo(() => {
    return () => (
      <DropdownIconComponent
        disableClearable={disableClearable}
        value={value}
        disabled={disabled}
        open={open}
        handleChange={handleChange}
        setOpen={setOpen}
      />
    );
  }, [disableClearable, value, disabled, open]);

  const selectStyles: SxProps = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Lato-Regular',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '16px',
      color: '#97A6A5',
      justifyContent: 'center',
      textAlign: 'center',
    },
    '& .MuiInputBase-input': {
      alignItems: 'center',
      border: '1px  #97A6A5',
      borderRadius: '4px',
      '&:focus': {
        backgroundColor: 'white',
      },
    },
    '& .MuiOutlinedInput-multiline': {
      borderColor: '#00C6B8',
    },
    '& .MuiOutlinedInput-input': {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
      },
    },
    fontFamily: 'Lato-Regular',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#00C6B8',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: disabled ? 'inherit' : '#00C6B8',
    },
    '& .MuiOutlinedInput-root': {
      borderColor: '#00C6B8',
      fontSize: '14px',
      height: '36px',
      fontFamily: 'Lato-Regular',
      '&:hover fieldset': {
        borderColor: onHoverBorderColor || '#00C6B8',
      },
      '&.Mui-focused fieldset': {
        borderColor: onFocusedBorderColor || '#00C6B8',
        borderWidth: '1px',
      },
    },
    '& label.MuiFormLabel-filled': {
      backgroundColor: placeholderOnfloatBackgroundColor ?? 'white',
      color: '#97A6A5',
    },
    '& label.Mui-focused': {
      backgroundColor: placeholderOnfloatBackgroundColor ?? 'transparent',
      color: '#00C6B8',
    },

    // Disabled styling
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: disabledColor || '#F2F2F2',
      cursor: 'not-allowed',
    },

    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: disabledColor || '#D0D0D0 !important',
    },

    '& .MuiOutlinedInput-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline':
      {
        borderColor: disabledColor || '#D0D0D0 !important',
      },

    '& .MuiOutlinedInput-root.Mui-disabled .MuiSelect-select': {
      cursor: 'not-allowed',
    },

    '& .MuiOutlinedInput-root.Mui-disabled .MuiSelect-icon': {
      cursor: 'not-allowed',
    },
    height: '36px',
    fontSize: '14px',
    backgroundColor: '#fff',
  };

  const badgePicker = (type: string) => {
    switch (type) {
      case 'appointmentType1':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-green-primary)',
              },
            }}
          />
        );
      case 'appointmentType2':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-pink)',
              },
            }}
          />
        );
      case 'appointmentType3':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-blue)',
              },
            }}
          />
        );
      case 'appointmentType4':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-red)',
              },
            }}
          />
        );
      case 'appointmentType5':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-yellow)',
              },
            }}
          />
        );
      case 'appointmentType6':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-purple)',
              },
            }}
          />
        );
      case 'appointmentType7':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-dark-blue)',
              },
            }}
          />
        );
      case 'appointmentType9':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-orange)',
              },
            }}
          />
        );
      case 'appointmentType10':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-violet)',
              },
            }}
          />
        );
      case 'appointmentType11':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'var(--thom-dark-green)',
              },
            }}
          />
        );
      default:
        return <CustomBadge variant="dot" />;
    }
  };

  return (
    <TextField
      id={id}
      select
      name={name}
      size="small"
      value={value}
      variant="outlined"
      required={Required}
      disabled={disabled}
      className={className + ' addReferralItemEllipsis'}
      defaultValue={defaultValue}
      fullWidth
      sx={{
        width: '100%',
        left: '0px',
        m: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...selectStyles,
      }}
      onChange={(e) => {
        if (handleChange) {
          handleChange(e.target.value);
        }
        setOpen(false);
      }}
      label={
        labelid &&
        rctl.formatMessage({
          id: labelid,
          defaultMessage: defaultlabelid,
        })
      }
      SelectProps={{
        IconComponent: IconComponent,
        MenuProps: MenuProps,
        onOpen: () => {
          setOpen(true);
        },
        onClose: () => {
          setOpen(false);
        },
        open: open,
      }}
    >
      {names.length === 0 ? (
        <MenuItem
          disabled
          sx={{
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            fontFamily: 'Lato-Regular',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '17px',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            ...sxProps,
          }}
        >
          <MediumTypography label={'No Data Available'} />
        </MenuItem>
      ) : (
        names.map((item: OptionType) => (
          <MenuItem
            key={item.id}
            value={item.id}
            defaultValue={item.id}
            sx={{
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              fontFamily: 'Lato-Regular',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '17px',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              ...sxProps,
            }}
          >
            {optionalBadge ? (
              <Box className="flex__">
                <Box style={{ alignSelf: 'center' }}>
                  {badgePicker(item.id)}
                </Box>
                <MediumTypography
                  label={item.label}
                  sxProps={{
                    paddingLeft: '10px',
                    color: '#000000de !important',
                  }}
                />
              </Box>
            ) : (
              <MediumTypography
                label={item.label}
                sxProps={{
                  color: item.color,
                }}
              />
            )}
          </MenuItem>
        ))
      )}
    </TextField>
  );
};

export default DropdownComponent;
